// BIZ-PLAYBOOKS (true-business-gaps §1 Process "Playbooks"): an SOP doc as a playbook. Under the
// doc's title, a compact strip: Owner (seat chip), Followed by (seat chips: a person's name, or a
// DE's with its DE badge), "Updated 2h ago by X", the runs ("3 of 4 followed", (i)), "N changes
// waiting" when a DE proposed a new version (to the approvals inbox), and the DEs that read it as
// knowledge. A hover pencil (the page's editors) opens a modal for the owner, followers and run
// templates (`PUT pages/{id}/playbook`); with templates linked, "Start run" makes a task from one
// and opens it. On the seat panel, `SeatPlaybooks` lists the seat's playbooks with followed %.
// RECALL-PROCEDURES: the "Procedure" chip (recorded runs, Recall state; run history in a modal).
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Field,
  FieldLabel,
  InfoTip,
  Label,
  NativeSelect,
  NativeSelectOption,
  QuickAddDialog,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import {
  queryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ListChecksIcon, PencilIcon, PlayIcon } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { problemText } from "../settings/problem-text";
import { ago } from "./doc-kinds";
import { itemTemplatesQuery } from "./item-templates";
import { useSeatPeople } from "./model";
import type { PageDetail } from "./page-dialogs";
import { ProcedureChip } from "./playbook-procedure";
import { boardsAllQuery, workKeys } from "./work-queries";

export type Playbook = OperationOutput<"pages.playbook">;
type PlaybookSeat = NonNullable<Playbook["owner"]>;

export const playbookKeys = {
  page: (co: string, id: string) => [...workKeys.all(co), "playbook", id] as const,
  seat: (co: string, seat: string) => [...workKeys.all(co), "playbooks", seat] as const,
};

export const playbookQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: playbookKeys.page(co, id),
    queryFn: () => api.client.pages.playbook({ params: { co, id } }),
    staleTime: 30_000,
  });

export const seatPlaybooksQuery = (api: Api, co: string, seat: string) =>
  queryOptions({
    queryKey: playbookKeys.seat(co, seat),
    queryFn: () => api.client.playbooks.list({ params: { co }, query: { seat_id: seat } }),
    staleTime: 30_000,
  });

/** A page is shown as a playbook when it is an SOP or anything is linked to it. */
export const isPlaybook = (p: Playbook) =>
  p.kind === "sop" || p.owner !== null || p.followers.length > 0 || p.templates.length > 0;

/** A seat as a chip: its holders (a DE with its badge), its title beside; an open seat by title. */
export function SeatChip({ seat }: { seat: PlaybookSeat }) {
  const holders = seat.holders;
  return (
    <span
      data-slot="seat-chip"
      title={seat.title}
      className="inline-flex h-6 max-w-64 items-center gap-1 rounded-full border border-subtle bg-card px-2 text-xs text-foreground"
    >
      {holders.length ? (
        holders.map((h) => (
          <span key={h.membership_id} className="inline-flex min-w-0 items-center gap-1">
            {h.kind === "digital_employee" ? <WorkerBadge kind="de" size="sm" /> : null}
            <span className="truncate font-medium">{workerName(h.display_name)}</span>
          </span>
        ))
      ) : (
        <span className="truncate font-medium">{seat.title}</span>
      )}
      <span className="truncate text-faint">{holders.length ? seat.title : "open"}</span>
    </span>
  );
}

function Item({ label, info, children }: { label: string; info?: ReactNode; children: ReactNode }) {
  return (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5">
      <span className="text-xs text-faint">{label}</span>
      {info ? <InfoTip label={`About ${label.toLowerCase()}`}>{info}</InfoTip> : null}
      {children}
    </span>
  );
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

function runsText(r: Playbook["runs"]): string {
  if (r.done > 0) return `${r.followed} of ${r.done} followed`;
  return r.started > 0 ? `${plural(r.started, "run")}, none done` : "No runs yet";
}

/**
 * The strip at the top of a doc (an SOP, or a page with playbook links); nothing for any other
 * page. It carries the knowledge readers, so the page's "Used by" line hides beside it.
 */
export function PlaybookStrip({
  api,
  company,
  page,
}: {
  api: Api;
  company: CompanyAccess;
  page: PageDetail;
}) {
  const co = company.id;
  const eligible = page.kind !== "task_body" && page.kind !== "board_note";
  const q = useQuery({ ...playbookQuery(api, co, page.id), enabled: eligible });
  const [editing, setEditing] = useState(false);
  const navigate = useNavigate();
  const start = useMutation({
    mutationFn: (t: Playbook["templates"][number]) =>
      api.client.tasks.create({
        params: { co },
        body: { board_id: t.board_id, template_id: t.id },
      }),
    onSuccess: (task) => {
      toast.add({ title: "Run started", description: task.title, type: "success" });
      void navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => ({ ...prev, task: task.id }),
      } as never);
    },
    onError: (e) =>
      toast.add({
        title: "The run did not start",
        description: problemText(e, "Try again."),
        type: "error",
      }),
  });
  const pb = q.data;
  if (!eligible || !pb || !isPlaybook(pb)) return null;
  const by = pb.updated_by;
  return (
    <section
      aria-label="Playbook"
      data-slot="playbook-strip"
      className="group/playbook mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-subtle bg-sunken/40 px-3 py-2 text-sm"
    >
      <Item label="Owner">
        {pb.owner ? <SeatChip seat={pb.owner} /> : <span className="text-muted-foreground">—</span>}
      </Item>
      <Item label="Followed by">
        {pb.followers.length ? (
          pb.followers.map((s) => <SeatChip key={s.seat_id} seat={s} />)
        ) : (
          <span className="text-muted-foreground">No one</span>
        )}
      </Item>
      <span className="text-xs text-muted-foreground" data-slot="playbook-updated">
        Updated {ago(pb.updated_at)}
        {by ? ` by ${workerName(by.display_name)}` : ""}
      </span>
      {pb.templates.length ? (
        <Item
          label="Runs"
          info="Runs started from this playbook's templates in 90 days; followed = every checklist item ticked."
        >
          <span className="text-xs font-medium tabular-nums" data-slot="playbook-runs">
            {runsText(pb.runs)}
          </span>
        </Item>
      ) : null}
      <ProcedureChip api={api} co={co} pageId={pb.page_id} />
      {pb.readers.length ? (
        <Item label="Read by" info="Agents whose knowledge includes this doc.">
          {pb.readers.map((r) => (
            <span
              key={r.membership_id}
              className="inline-flex items-center gap-1 text-xs text-foreground"
            >
              <WorkerBadge kind={r.kind === "digital_employee" ? "de" : "ag"} size="sm" />
              {workerName(r.display_name)}
            </span>
          ))}
        </Item>
      ) : null}
      {pb.pending_updates > 0 ? (
        <Link
          to="/c/$slug/agents/approvals"
          params={{ slug: company.slug }}
          search={keepFocus}
          data-slot="playbook-pending"
          className="rounded-full focus-visible:outline-2 focus-visible:outline-ring"
        >
          <Badge variant="warning">
            {pb.pending_updates === 1
              ? "1 change waiting"
              : `${pb.pending_updates} changes waiting`}
          </Badge>
        </Link>
      ) : null}
      <span className="ml-auto flex items-center gap-1">
        {pb.templates.length === 1 && pb.templates[0] ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={start.isPending}
            onClick={() => pb.templates[0] && start.mutate(pb.templates[0])}
          >
            <PlayIcon aria-hidden="true" />
            Start run
          </Button>
        ) : pb.templates.length > 1 ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              render={<Button variant="ghost" size="sm" disabled={start.isPending} />}
            >
              <PlayIcon aria-hidden="true" />
              Start run
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-56">
              {pb.templates.map((t) => (
                <DropdownMenuItem key={t.id} onClick={() => start.mutate(t)}>
                  <ListChecksIcon aria-hidden="true" />
                  {t.name}
                  <span className="ml-auto text-xs text-faint">{t.board_name}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
        {pb.can_edit ? (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Edit playbook"
            title="Edit playbook"
            className="opacity-0 group-hover/playbook:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
            onClick={() => setEditing(true)}
          >
            <PencilIcon aria-hidden="true" />
          </Button>
        ) : null}
      </span>
      {editing ? (
        <PlaybookDialog
          api={api}
          company={company}
          playbook={pb}
          onClose={() => setEditing(false)}
        />
      ) : null}
    </section>
  );
}

function CheckRow({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <li className="flex min-w-0 items-center gap-2 py-1">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      <Label htmlFor={id} className="min-w-0 flex-1 truncate font-normal">
        {children}
      </Label>
    </li>
  );
}

/** A seat as a picker option: "Riley — Recruiting coordinator (DE)", "CEO (open)". */
export function seatText(s: { seat: string; name: string; open: boolean; de: boolean }): string {
  if (s.open) return `${s.seat} (open)`;
  const de = s.de && !/\(DE\)/.test(s.seat) ? " (DE)" : "";
  return `${s.name} — ${s.seat}${de}`;
}

const toggle = (list: string[], id: string, on: boolean) =>
  on ? [...new Set([...list, id])] : list.filter((x) => x !== id);

/** Owner, followers and run templates (the whole set, `PUT pages/{id}/playbook`). */
function PlaybookDialog({
  api,
  company,
  playbook,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  playbook: Playbook;
  onClose: () => void;
}) {
  const co = company.id;
  const queryClient = useQueryClient();
  const people = useSeatPeople(api, co);
  const seats = people.all.filter((s) => !s.closed);
  const boards = useQuery(boardsAllQuery(api, co));
  const live = (boards.data ?? []).filter((b) => !b.archived_at);
  const templates = useQueries({
    queries: live.map((b) => itemTemplatesQuery(api, co, b.id)),
  }).flatMap((r, i) =>
    (r.data?.data ?? []).map((t) => ({ id: t.id, name: t.name, board: live[i]?.name ?? "" })),
  );
  const [owner, setOwner] = useState(playbook.owner?.seat_id ?? "");
  const [followers, setFollowers] = useState(playbook.followers.map((f) => f.seat_id));
  const [picked, setPicked] = useState(playbook.templates.map((t) => t.id));
  const [approval, setApproval] = useState(playbook.owner_approval);
  const ownerId = useId();
  const save = useMutation({
    mutationFn: () =>
      api.client.pages.playbookSet({
        params: { co, id: playbook.page_id },
        body: {
          owner_seat_id: owner || null,
          follower_seat_ids: followers,
          item_template_ids: picked,
          owner_approval: approval,
        },
      }),
    onSuccess: async (pb) => {
      queryClient.setQueryData(playbookKeys.page(co, pb.page_id), pb);
      await queryClient.invalidateQueries({ queryKey: [...workKeys.all(co), "playbooks"] });
      toast.add({ title: "Playbook saved", type: "success" });
      onClose();
    },
  });
  return (
    <QuickAddDialog
      title="Playbook"
      info="Who owns this doc, the seats that follow it (people or DEs), and the task templates a run starts from."
      description={playbook.title}
      submitLabel="Save"
      pending={save.isPending}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      slot="playbook-dialog"
      error={
        save.error ? (
          <p role="alert" className="text-sm text-danger-text">
            {problemText(save.error, "The playbook was not saved.")}
          </p>
        ) : null
      }
    >
      <Field>
        <FieldLabel htmlFor={ownerId}>Owner</FieldLabel>
        <NativeSelect id={ownerId} value={owner} onChange={(e) => setOwner(e.target.value)}>
          <NativeSelectOption value="">No owner</NativeSelectOption>
          {seats.map((s) => (
            <NativeSelectOption key={s.seatId} value={s.seatId}>
              {seatText(s)}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Field>
      <fieldset className="flex min-w-0 flex-col gap-1">
        <legend className="mb-1 flex items-center gap-1 text-sm font-medium">
          Followed by
          <InfoTip label="About followed by">
            The seats that run this playbook. A DE on one reads it before each run.
          </InfoTip>
        </legend>
        <ul
          aria-label="Follower seats"
          className="m-0 max-h-48 list-none overflow-y-auto rounded-md border border-subtle px-2 py-1 [scrollbar-width:thin]"
        >
          {seats.map((s) => (
            <CheckRow
              key={s.seatId}
              checked={followers.includes(s.seatId)}
              onChange={(on) => setFollowers((f) => toggle(f, s.seatId, on))}
            >
              {seatText(s)}
            </CheckRow>
          ))}
        </ul>
      </fieldset>
      <fieldset className="flex min-w-0 flex-col gap-1">
        <legend className="mb-1 flex items-center gap-1 text-sm font-medium">
          Run templates
          <InfoTip label="About run templates">
            A run is a task made from one of these templates; its checklist is the playbook's steps.
          </InfoTip>
        </legend>
        {templates.length ? (
          <ul
            aria-label="Run templates"
            className="m-0 max-h-40 list-none overflow-y-auto rounded-md border border-subtle px-2 py-1 [scrollbar-width:thin]"
          >
            {templates.map((t) => (
              <CheckRow
                key={t.id}
                checked={picked.includes(t.id)}
                onChange={(on) => setPicked((p) => toggle(p, t.id, on))}
              >
                {t.name} <span className="text-faint">· {t.board}</span>
              </CheckRow>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No task templates yet</p>
        )}
      </fieldset>
      <ul className="m-0 list-none p-0">
        <CheckRow checked={approval} onChange={setApproval}>
          <span className="inline-flex items-center gap-1">
            Changes need owner approval
            <InfoTip label="About owner approval">
              DEs can't edit this doc directly. They propose a change and the owner approves it.
            </InfoTip>
          </span>
        </CheckRow>
      </ul>
    </QuickAddDialog>
  );
}

/** The seat panel's "Playbooks": the seat's playbooks (owned or followed), as links. */
export function SeatPlaybooks({
  api,
  company,
  seatId,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
}) {
  const q = useQuery(seatPlaybooksQuery(api, company.id, seatId));
  return (
    <section
      data-slot="seat-playbooks"
      aria-label="Playbooks"
      className="flex flex-col gap-1.5 border-t border-subtle pt-3"
    >
      <div className="flex min-h-6 items-center gap-1">
        <h3 className="text-[11px] leading-4 font-medium tracking-wide text-faint uppercase">
          Playbooks
        </h3>
        <InfoTip label="About playbooks" className="size-4">
          SOP docs this seat owns or follows; % = runs that ticked every checklist item.
        </InfoTip>
      </div>
      {q.isPending ? null : !q.data?.data.length ? (
        <p className="text-xs text-muted-foreground">No playbooks</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {q.data.data.map((p) => (
            <li key={p.page_id} className="flex min-w-0 items-center gap-2">
              <ListChecksIcon aria-hidden="true" className="size-3.5 shrink-0 text-faint" />
              <Link
                to="/c/$slug/work/p/$pageId"
                params={{ slug: company.slug, pageId: p.page_id }}
                search={keepFocus}
                className="min-w-0 flex-1 truncate text-sm text-secondary hover:text-foreground hover:underline"
              >
                {p.title}
              </Link>
              {p.owner?.seat_id === seatId ? (
                <span className="text-[11px] text-faint">Owner</span>
              ) : null}
              <span className="text-xs text-muted-foreground tabular-nums">
                {p.followed_pct === null ? "—" : `${p.followed_pct}%`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
